import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  User,
  Bot,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Expand,
  Check,
} from "lucide-react";
import { Message as MessageType } from "../../types";
import gfm from "remark-gfm";

interface MessageProps {
  message: MessageType;
  isMaximized?: boolean;
  themeMode?: "light" | "dark";
}

export const Message: React.FC<MessageProps> = ({
  message,
  isMaximized = false,
  themeMode = "light",
}) => {
  const [showActions, setShowActions] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reaction, setReaction] = useState<"thumbs-up" | "thumbs-down" | null>(
    null
  );

  const isUser = message.sender === "user";
  const isSQL = message.metadata?.query_type === "sql";
  const isLongMessage = message.content.length > 300;

  const isDark = themeMode === "dark";

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Hide after 2 seconds
  };

  const handleReaction = (type: "thumbs-up" | "thumbs-down") => {
    setReaction(reaction === type ? null : type);
  };

  return (
    <div
      className={`group flex gap-3 ${isUser ? "flex-row-reverse" : "flex-row"} mb-6`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser
            ? "bg-orange-500 text-white"
            : message.error
              ? isDark
                ? "bg-red-900 text-red-400"
                : "bg-red-100 text-red-600"
              : isSQL
                ? isDark
                  ? "bg-[#1C1F24] text-orange-400"
                  : "bg-orange-100 text-orange-600"
                : isDark
                  ? "bg-[#1C1F24] text-gray-300"
                  : "bg-gray-100 text-gray-600"
        }`}
      >
        {isUser ? (
          <User className="w-4 h-4" />
        ) : (
          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
            <Bot className="w-4 h-4 text-orange-600" />
          </div>
        )}
      </div>

      {/* Message Content Container */}
      <div
        className={`flex flex-col max-w-[85%] sm:max-w-[80%] md:max-w-[75%] ${isUser ? "items-end" : "items-start"}`}
      >
        {/* Message Bubble */}
        <div
          className={`px-4 py-2 rounded-2xl ${
            isUser
              ? "bg-orange-500 text-white rounded-br-sm"
              : message.error
                ? isDark
                  ? "bg-red-600/20 text-red-300 border border-red-400 rounded-bl-sm"
                  : "bg-red-50 text-red-800 border border-red-200 rounded-bl-sm"
                : isDark
                  ? "bg-[#1C1F24] text-gray-500 border border-[#1C1F24] rounded-bl-sm"
                  : "bg-gray-100 text-gray-800 rounded-bl-sm"
          }`}
        >
          <div
            className={`text-sm p max-w-none prose prose-sm ${isDark ? "prose-invert" : ""}`}
          >
            <ReactMarkdown remarkPlugins={[gfm]}>
              {message.content}
            </ReactMarkdown>
          </div>
        </div>

        {/* Actions and Timestamp Row */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            {/* Timestamp */}
            <span
              className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              {message.timestamp.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </span>

            {/* Processing Time */}
            {message.metadata?.processing_time &&
              message.metadata.processing_time > 2 &&
              !isUser && (
                <div
                  className={`flex items-center gap-1 text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  <Clock className="w-3 h-3" />
                  <span>{message.metadata.processing_time.toFixed(1)}s</span>
                </div>
              )}
          </div>

          {/* Message Actions */}
          {!isUser && (
            <div
              className={`flex items-center gap-1 transition-opacity duration-200 ${
                showActions || reaction
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none"
              }`}
            >
              <button
                onClick={() => handleReaction("thumbs-up")}
                className={`p-1 rounded-md transition-all ${
                  isDark
                    ? "text-gray-400 hover:text-white hover:bg-[#1C1F24]/60"
                    : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                } ${reaction === "thumbs-up" ? "bg-green-50 text-green-600" : ""}`}
                title="Helpful"
              >
                <ThumbsUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleReaction("thumbs-down")}
                className={`p-1 rounded-md hover:bg-gray-100 transition-colors ${
                  reaction === "thumbs-down"
                    ? "bg-red-50 text-red-600"
                    : "text-gray-400"
                }`}
                title="Not helpful"
              >
                <ThumbsDown className="w-3 h-3" />
              </button>
              <button
                onClick={handleCopy}
                className={`p-1 rounded-md hover:bg-gray-100 transition-colors ${
                  copied ? "bg-green-50 text-green-600" : "text-gray-400"
                }`}
                title={copied ? "Copied!" : "Copy message"}
              >
                {copied ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
              {/* {isLongMessage && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-400"
                  title={expanded ? "Collapse" : "Expand"}
                >
                  <Expand className="w-3 h-3" />
                </button>
              )} */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
